import { ENV } from '@config/env';
import { FEATURES } from '@config/features';
import { STAGE_STATUS } from '../../constants/constants';
import { API_BASE_URL, SOCKET_URL } from '../../constants/domain';
import * as meter from './meter';

/**
 * Development-only network usage meter — the gate and the console API.
 * See ./meter.js for what is measured and how, and
 * inPerformance/docs/network-meter.md for how to run a measurement.
 *
 * Enabled only when FEATURES.NETWORK_METER is on AND the environment is not
 * production; otherwise nothing is patched and every export below is a no-op.
 */
export const isNetworkMeterEnabled = () =>
  Boolean(FEATURES.NETWORK_METER) && !ENV.isProduction && typeof window !== 'undefined';

// One-shot autostart: set before a reload so the interval begins at page load.
//   localStorage.setItem('netmeter:autostart', 'first-use'); location.reload();
// or open any URL with ?netmeter_autostart=<scenario>.
const AUTOSTART_KEY = 'netmeter:autostart';
const AUTOSTART_PARAM = 'netmeter_autostart';

function readAutostart() {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get(AUTOSTART_PARAM);
    if (fromQuery) {
      return fromQuery;
    }
    const fromStorage = window.localStorage.getItem(AUTOSTART_KEY);
    if (fromStorage) {
      window.localStorage.removeItem(AUTOSTART_KEY);
      return fromStorage;
    }
  } catch (_error) {
    // Storage blocked: no autostart.
  }
  return null;
}

/** Called once from main.jsx, before anything opens a socket or a peer connection. */
export function installNetworkMeter() {
  if (!isNetworkMeterEnabled()) {
    return;
  }
  try {
    meter.installMeter({
      pageOrigin: window.location.origin,
      apiBaseUrl: API_BASE_URL,
      poseSocketUrl: SOCKET_URL,
      isLocalDevServer: ENV.isLocal,
    });
    meter.setStageNames(STAGE_STATUS);

    window.__netMeter = Object.freeze({
      start: (options = {}) => meter.start(options),
      stop: () => meter.stop(),
      reset: () => meter.reset(),
      note: text => meter.note(text),
      report: () => meter.report(),
      exportJson: () => JSON.stringify(meter.report(), null, 2),
      exportCsv: () => meter.csv(),
      downloadJson: () => meter.downloadJson(),
      downloadCsv: () => meter.downloadCsv(),
      isRunning: () => meter.isRunning(),
      autostartNextLoad: scenario => {
        window.localStorage.setItem(AUTOSTART_KEY, scenario || 'session');
      },
    });

    const autostart = readAutostart();
    if (autostart) {
      meter.start({ scenario: autostart, fromNavigationStart: true });
    }
    console.warn(
      '[network-meter] installed (development only). window.__netMeter / Ctrl+Alt+M.' +
        (autostart ? ` Autostarted: ${autostart}.` : '')
    );
  } catch (error) {
    console.warn('[network-meter] install failed; the app is unaffected.', error);
  }
}

/** The pose socket hook reports the current STAGE_STATUS value; no-op when disabled. */
export function reportNetworkMeterStage(stage) {
  if (isNetworkMeterEnabled()) {
    meter.setStage(stage);
  }
}

export const networkMeter = meter;
